import { EventDeliveryStatus } from './types/helperTypes/EventDeliveryStatus';
import { isConfirmableEvent, SERPConfirmationEventType, SERPEvent } from './types/SERPEvents';

export class EventEmitter {
    constructor() {
        this.emitEvent = this.emitEvent.bind(this);
        this.emitConfirmableEvent = this.emitConfirmableEvent.bind(this);
    }

    /**
     * Emits event to the window object
     * does not block execution
     */
    public emitEvent(event: SERPEvent): void {
        window.dispatchEvent(event);
    }

    /**
     * Emits event to the window object
     * blocks execution till event delivery is confirmed by the consumer or timeout is reached
     */
    public async emitConfirmableEvent(event: SERPEvent, timeout = 250): Promise<Array<EventDeliveryStatus>> {
        if (!isConfirmableEvent(event)) {
            throw new Error('Event is not confirmable. Confirm codes are not provided');
        }

        const deliveryConfirmation = this.waitForMultipleEventConfirmations(
            event.detail.deliveryConfirmations,
            timeout
        );
        this.emitEvent(event);

        return deliveryConfirmation;
    }

    private async waitForEventConfirmation(
        confirmationCode: `${SERPConfirmationEventType}`,
        timeout: number
    ): Promise<EventDeliveryStatus> {
        return new Promise((resolve) => {
            const timeoutId = setTimeout(() => {
                window.removeEventListener(confirmationCode, listener);
                resolve({ isTimeouted: true, isDelivered: false });
            }, timeout);

            const listener = () => {
                window.removeEventListener(confirmationCode, listener);
                clearTimeout(timeoutId);
                resolve({ isDelivered: true, isTimeouted: false });
            };
            window.addEventListener(confirmationCode, listener);
        });
    }

    private waitForMultipleEventConfirmations = async (
        deliveryConfirmations: Array<`${SERPConfirmationEventType}`>,
        timeout: number
    ): Promise<Array<EventDeliveryStatus>> => {
        const arr = deliveryConfirmations.map((confirmationCode) =>
            this.waitForEventConfirmation(confirmationCode, timeout)
        );
        return Promise.all(arr).then((results) => results);
    };
}
